import { useState } from 'react';
import { X, Calculator, Cpu, Monitor, HardDrive, Zap, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';

interface BuildEstimatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Component definitions with icons and labels
const COMPONENT_DEFINITIONS = {
  cpu: {
    id: 'cpu' as const,
    label: 'CPU',
    icon: Cpu,
    color: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50',
    textLight: 'text-violet-700',
  },
  gpu: {
    id: 'gpu' as const,
    label: 'GPU',
    icon: Monitor,
    color: 'from-emerald-500 to-teal-600',
    bgLight: 'bg-emerald-50',
    textLight: 'text-emerald-700',
  },
  ram: {
    id: 'ram' as const,
    label: 'RAM',
    icon: Zap,
    color: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50',
    textLight: 'text-amber-700',
  },
  ssd: {
    id: 'ssd' as const,
    label: 'SSD',
    icon: HardDrive,
    color: 'from-blue-500 to-cyan-600',
    bgLight: 'bg-blue-50',
    textLight: 'text-blue-700',
  },
};

// Quick budget presets
const BUDGET_PRESETS = [
  { name: '사무용', price: 500000, icon: '💼' },
  { name: '게이밍', price: 1500000, icon: '🎮' },
  { name: '고사양', price: 3000000, icon: '🚀' },
];

export function BuildEstimatorModal({ isOpen, onClose }: BuildEstimatorModalProps) {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Calculator className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">PC 견적내기</h2>
                <p className="text-blue-100 text-sm">빠르게 예상 견적을 확인해보세요</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="닫기"
            >
              <X className="text-white" size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Budget Presets */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 빠른 견적 선택</h3>
              <div className="grid grid-cols-3 gap-3">
                {BUDGET_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => setSelectedPreset(preset.price)}
                    className={clsx(
                      'p-4 rounded-xl border-2 transition-all',
                      selectedPreset === preset.price
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    )}
                  >
                    <div className="text-2xl mb-1">{preset.icon}</div>
                    <div className="font-medium text-gray-900">{preset.name}</div>
                    <div className="text-sm text-gray-500">
                      {(preset.price / 10000).toFixed(0)}만원~
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Component Examples */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔧 주요 부품 가격대</h3>
              <div className="space-y-3">
                {Object.entries(COMPONENT_DEFINITIONS).map(([key, def]) => {
                  const Icon = def.icon;
                  const priceRanges: Record<string, [number, number]> = {
                    cpu: [100000, 500000],
                    gpu: [200000, 2000000],
                    ram: [50000, 300000],
                    ssd: [50000, 300000],
                  };
                  const [min, max] = priceRanges[key] || [0, 0];

                  return (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${def.bgLight} rounded-lg`}>
                          <Icon className={def.textLight} size={20} />
                        </div>
                        <span className="font-medium text-gray-900">{def.label}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {(min / 10000).toFixed(0)}만 ~ {(max / 10000).toFixed(0)}만원
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 견적 팁</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• CPU와 GPU 예산이 전체의 50~60%를 차지합니다</li>
                <li>• 게이밍용은 GPU, 사무용은 CPU/RAM에 더 투자하세요</li>
                <li>• SSD는 NVMe가 SATA보다 2~3배 빠릅니다</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">예상 총액</p>
                <p className="text-2xl font-bold text-gray-900">
                  {selectedPreset
                    ? `${(selectedPreset / 10000).toFixed(0)}만원`
                    : '선택해주세요'}
                </p>
              </div>
              <button
                onClick={() => {
                  // TODO: Navigate to full build estimator page
                  window.location.href = selectedPreset
                    ? `/products?minPrice=${Math.round(selectedPreset * 0.8)}&maxPrice=${Math.round(selectedPreset * 1.2)}`
                    : '/products';
                }}
                disabled={!selectedPreset}
                className={clsx(
                  'flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all',
                  selectedPreset
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                )}
              >
                제품 보기
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
